import { Helmet } from 'react-helmet-async';
import Lottie from 'react-lottie';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import s from './PersonalDetailsContinue.module.scss';
import translate from 'utils/translate';
import LoadingAnimation from 'assets/lottie/loading-vertical-lines.json';
import { Typography } from 'components';
import styles from '../../../../variables.module.scss';
import {
  MANUAL_REVIEW,
  PHONE_VERIFICATION,
  REJECTED_OFFER,
} from 'Router/routes';
import useIovation from 'hooks/useIovation';
import {
  usePrecheckApplicationMutation,
  useAffiliate,
  useApplicationQuery,
  useClientOfferQuery,
  useCreateApplicationWithoutRiskAndSendGACookiesMutation,
} from 'hooks/webapi';
import { useClientQuery } from 'hooks/webapi/client/useClientQuery';
import { getClientEmail } from 'hooks/webapi/client/useClientEmailQuery';
import { getClientPhone } from 'hooks/webapi/client/useClientPhoneQuery';
import useCalculator from 'hooks/calculator/useCalculator';
import IdentifyEvent from 'hooks/context/EventCollector/events/IdentifyEvent';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import { useClientApplicationCheckStatusQuery } from 'hooks/webapi/client/useClientApplicationCheckStatusQuery';
import { useClientConstraintsQuery } from 'hooks/webapi/application/useUpsale';
import useSeon from 'hooks/useSeon';
import { session } from 'session';

const LottieOptions = {
  loop: true,
  autoplay: true,
  animationData: LoadingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export default function PersonalDetailsContinue() {
  const { mutateAsync: precheckApplication } = usePrecheckApplicationMutation();
  const { mutateAsync: createClientApplicationWithoutRisk } =
    useCreateApplicationWithoutRiskAndSendGACookiesMutation();
  const { sendSeonFraudInfo } = useSeon();
  const { loadAmountFromSessionStorage, amount } = useCalculator();
  const { data: clientConstraintsData, isLoading: clientConstraintsIsLoading } =
    useClientConstraintsQuery();
  const { refetch: getClient } = useClientQuery();
  const { data: clientOffer } = useClientOfferQuery(
    {
      amount,
      term: clientConstraintsData?.termInterval.defaultValue,
    },
    Boolean(amount) && clientConstraintsIsLoading,
  );

  const [isPollingApplicationStatus, setIsPollingApplicationStatus] =
    useState(false);
  const navigate = useNavigate();
  const { identify } = useEventCollector();
  const { affiliate } = useAffiliate();
  const { ioBlackBox } = useIovation();

  useEffect(() => {
    session.confirmLogin();
    loadAmountFromSessionStorage();
  }, []);

  useEffect(() => {
    (async function processClientAfterAuth() {
      const [client, emailData, phone] = await Promise.all([
        getClient(),
        getClientEmail(),
        getClientPhone(),
      ]);

      identify(
        new IdentifyEvent({
          phone,
          email: emailData,
          client: client.data,
        }),
      );
    })();
  }, []);

  useEffect(() => {
    (async function createApplicationWithPrecheck() {
      if (!clientOffer) {
        return;
      }

      await createClientApplicationWithoutRisk({
        amount: clientOffer.newPrincipal,
        term: clientOffer.term,
        type: 'WEB',
        source: 'DESKTOP',
        affiliateInfo: affiliate,
        ioBlackBox,
      });

      const precheck = await precheckApplication();

      if (precheck.rejected) {
        navigate(REJECTED_OFFER);
        return;
      }
      sendSeonFraudInfo();
      setIsPollingApplicationStatus(true);
    })();
  }, [affiliate, clientOffer]);

  const { data: applicationCheckStatusData } =
    useClientApplicationCheckStatusQuery(isPollingApplicationStatus, 3000);
  const { data: applicationData } = useApplicationQuery(
    applicationCheckStatusData?.completed,
  );

  useEffect(() => {
    if (!applicationData) {
      return;
    }

    setIsPollingApplicationStatus(false);

    identify(new IdentifyEvent({ application: applicationData }));
    if (
      applicationData.status === 'OPEN' &&
      applicationData.resolution === 'MANUAL'
    ) {
      navigate(MANUAL_REVIEW);
    }

    if (
      applicationData.status === 'OPEN' &&
      applicationData.resolution !== 'MANUAL'
    ) {
      navigate(PHONE_VERIFICATION);
      return;
    }

    if (applicationData.resolution === 'REJECTED') {
      navigate(REJECTED_OFFER);
    }
  }, [applicationData]);

  return (
    <>
      <Helmet>
        <title>{translate('waiting.html_title')}</title>
      </Helmet>
      <div className={styles['box-wrapper']}>
        <div className={s.lottie}>
          <Lottie width={200} options={LottieOptions} />
        </div>
        <Typography className={s.title} kind="h1">
          {translate('waiting.title')}
        </Typography>
        <Typography className={s.info}>{translate('waiting.info')}</Typography>
      </div>
    </>
  );
}
